import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ArrowRight, ArrowLeft, X, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * GuidedTour — lightweight onboarding overlay that walks new Free users
 * through the system. Highlights a target element with a spotlight + tooltip card.
 *
 * Steps reference data-testid selectors so the tour adapts even if layout shifts.
 * The tour persists "seen" state in localStorage AND a server flag (tour_completed)
 * so it never re-shows once dismissed.
 */

const STORAGE_KEY = "arissai.tour.completed.v1";

const STEPS = [
  {
    target: "[data-testid=\"app-shell\"]",
    placement: "center",
    title: "Welcome to ARISSAI",
    body: "Let's take a 60-second tour. We'll show you how the six AI lenses turn project data into decisions.",
    cta: "Start the tour",
  },
  {
    target: "[data-testid=\"free-demo-banner\"]",
    placement: "bottom",
    title: "You're on the Free plan",
    body: "We've pre-loaded a sample project — \"Demo · Cloud Migration\" — so you can explore every lens before you commit. No upload, no card.",
  },
  {
    target: "[data-testid=\"project-card\"]",
    placement: "right",
    title: "Open the demo project",
    body: "Click any project card to dive in. The demo already has realistic risks, issues and assumptions for a typical cloud migration.",
  },
  {
    target: "[data-testid=\"ai-usage-banner\"]",
    placement: "bottom",
    title: "Track your AI usage here",
    body: "Every Smart Ingest upload, AI Enrich, Bulk Generate, and Assistant chat counts toward your monthly quota. Free tier has 0 — upgrade to Starter ($9.99/mo) to start using AI.",
    optional: true,
  },
  {
    target: "[data-testid=\"trial-banner\"], [data-testid=\"free-demo-banner\"]",
    placement: "bottom",
    title: "Ready to go beyond the demo?",
    body: "Starter unlocks 1 real project, Smart Ingest, AI Enrich and 5 AI chat messages per month — all for $9.99 CAD.",
    cta: "See plans",
    href: "/pricing",
  },
];

export default function GuidedTour() {
  const { user, updateUser } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [visible, setVisible] = useState(false);

  const isFree = !!user && (user.subscription_tier === "free" || !user.subscription_tier) && !user.is_admin;
  const seenLocal = useMemo(() => {
    if (typeof window === "undefined") return true;
    return !!window.localStorage.getItem(STORAGE_KEY);
  }, []);
  const seenServer = !!user?.tour_completed;

  // Tour is opt-in: only triggers when ?tour=1 is in the URL (set by the
  // "Re-take the tour" button on /account). New users land directly on the
  // demo project — no overlay forced on signup.
  useEffect(() => {
    if (!isFree) return;
    if (typeof window === "undefined") return;
    const wantsTour = new URLSearchParams(window.location.search).get("tour") === "1";
    if (!wantsTour) return;
    if (seenLocal && seenServer) return;
    const t = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(t);
  }, [isFree, seenLocal, seenServer]);

  // Compute the spotlight rect for the current step.
  useEffect(() => {
    if (!visible) return;
    const step = STEPS[stepIdx];
    if (!step) return;
    if (step.placement === "center") {
      setRect(null);
      return;
    }
    const compute = () => {
      const el = document.querySelector(step.target);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [stepIdx, visible]);

  const close = useCallback(async () => {
    setVisible(false);
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, "1");
      }
    } catch { /* ignore storage errors */ }
    try {
      // Backend flag so the tour doesn't re-show on another device
      const api = (await import("@/lib/api")).default;
      await api.patch("/auth/me", { tour_completed: true });
      updateUser?.({ ...user, tour_completed: true });
    } catch { /* non-blocking */ }
  }, [updateUser, user]);

  const next = useCallback(() => {
    if (stepIdx >= STEPS.length - 1) {
      close();
    } else {
      setStepIdx((p) => p + 1);
    }
  }, [stepIdx, close]);

  const prev = useCallback(() => {
    setStepIdx((p) => Math.max(0, p - 1));
  }, []);

  if (!visible) return null;

  const step = STEPS[stepIdx];
  const total = STEPS.length;
  const isFirst = stepIdx === 0;
  const isLast = stepIdx === total - 1;

  // Tooltip card position
  let cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  if (rect && step.placement !== "center") {
    const pad = 16;
    const cardW = 380;
    const placement = step.placement || "bottom";
    if (placement === "bottom") {
      cardStyle = {
        top: rect.top + rect.height + pad + window.scrollY,
        left: Math.min(window.innerWidth - cardW - 16, Math.max(16, rect.left + rect.width / 2 - cardW / 2)),
        transform: "none",
      };
    } else if (placement === "top") {
      cardStyle = {
        top: Math.max(16, rect.top - pad - 240 + window.scrollY),
        left: Math.min(window.innerWidth - cardW - 16, Math.max(16, rect.left + rect.width / 2 - cardW / 2)),
        transform: "none",
      };
    } else if (placement === "right") {
      cardStyle = {
        top: Math.max(16, rect.top + window.scrollY),
        left: Math.min(window.innerWidth - cardW - 16, rect.left + rect.width + pad),
        transform: "none",
      };
    }
  }

  return (
    <div
      className="fixed inset-0 z-[200]"
      data-testid="guided-tour-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={step.title}
    >
      {/* Dim everything except the spotlight */}
      {rect && step.placement !== "center" ? (
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" style={{ width: "100vw", height: "100vh" }}>
          <defs>
            <mask id="tour-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={rect.left - 8}
                y={rect.top - 8}
                width={rect.width + 16}
                height={rect.height + 16}
                rx="6"
                fill="black"
              />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(9,9,11,0.72)" mask="url(#tour-mask)" />
          <rect
            x={rect.left - 8}
            y={rect.top - 8}
            width={rect.width + 16}
            height={rect.height + 16}
            rx="6"
            fill="none"
            stroke="#0020F5"
            strokeWidth="2"
          />
        </svg>
      ) : (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      )}

      {/* Tooltip card */}
      <div
        className="absolute w-[380px] max-w-[92vw] bg-white border-2 border-zinc-950 shadow-2xl"
        style={cardStyle}
        data-testid={`guided-tour-step-${stepIdx + 1}`}
      >
        <div className="flex items-start justify-between px-5 pt-4">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">
            <Sparkle size={12} weight="fill" /> Step {stepIdx + 1} of {total}
          </div>
          <button
            onClick={close}
            className="p-1 -mr-1 -mt-1 text-zinc-500 hover:text-zinc-950"
            aria-label="Close tour"
            data-testid="guided-tour-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="px-5 pb-4 pt-2">
          <h3 className="font-display font-bold text-lg leading-snug text-zinc-950">{step.title}</h3>
          <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{step.body}</p>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-200 bg-zinc-50">
          {!isFirst ? (
            <button
              onClick={prev}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-700 hover:text-zinc-950"
              data-testid="guided-tour-prev"
            >
              <ArrowLeft size={12} weight="bold" /> Back
            </button>
          ) : (
            <button
              onClick={close}
              className="text-xs font-semibold text-zinc-500 hover:text-zinc-950"
              data-testid="guided-tour-skip"
            >
              Skip tour
            </button>
          )}
          {step.href ? (
            <a
              href={step.href}
              onClick={() => close()}
              className="inline-flex items-center gap-2 h-9 px-4 bg-zinc-950 text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5]"
              data-testid="guided-tour-cta-link"
            >
              {step.cta || "Continue"} →
            </a>
          ) : (
            <button
              onClick={next}
              className="inline-flex items-center gap-2 h-9 px-4 bg-zinc-950 text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5]"
              data-testid="guided-tour-next"
            >
              {isLast ? (
                <>
                  <CheckCircle size={12} weight="fill" /> Done
                </>
              ) : (
                <>
                  {step.cta || "Next"} <ArrowRight size={12} weight="bold" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
